import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Nutrient } from 'views/feed-management/features/nutrients/types/nutrient.type';
import { NutrientsAttribute } from 'views/feed-management/features/nutrients/types/nutrients-attribute.type';

import { Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import Breadcrumbs from 'components/breadcrumbs';
import Error from 'components/error';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';
import {
  getFeedManagementNutrients,
  getFeedManagementNutrientsAttribute,
} from 'views/feed-management/features/nutrients/api/get-nutrients';
import { putFeedManagementNutrientsAttribute } from 'views/feed-management/features/nutrients/api/update-nutrients';
import FeedManagementTabsHeader from '../../components/feed-management-tabs-header';

type FormValues = {
  Nutrients: {
    attributeCode: string;
    nutrient: Nutrient | null;
  }[];
};

type Props = {};

const ListNutrients: React.FC<Props> = () => {
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const theme = useTheme();

  const PAGE_NAME_ID = 'feedManagement.listNutrients';

  // FORMS
  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      Nutrients: [],
    },
    shouldUnregister: false,
  });

  const { fields } = useFieldArray({
    control: control,
    name: 'Nutrients',
    shouldUnregister: false,
  });

  // FETCH DATA
  const { data: nutrients } = useGet(['getFeedManagementNutrients'], () => getFeedManagementNutrients());

  const { data, isLoading, error, isError } = useGet(
    ['getFeedManagementNutrientsAttribute'],
    () => getFeedManagementNutrientsAttribute(),
    {
      enabled: !!nutrients,
    }
  );

  useEffect(() => {
    if (!data || !nutrients) return;

    reset({
      Nutrients: data.map((i) => ({
        attributeCode: i.attributeCode,
        nutrient: nutrients.find((k) => k.nutrientCode === i.nutrientCode) ?? null,
      })),
    });
  }, [data, nutrients, reset]);

  // POST DATA
  const updateNutrientsAttribute = usePost<NutrientsAttribute[], NutrientsAttribute[]>(
    (request) => putFeedManagementNutrientsAttribute(request),
    (response) => {
      reset({
        Nutrients: response.map((i) => ({
          attributeCode: i.attributeCode,
          nutrient: nutrients?.find((k) => k.nutrientCode === i.nutrientCode) ?? null,
        })),
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDELRS
  const handleUpdateNutrientsAttribute = (data: FormValues) => {
    updateNutrientsAttribute.mutate(
      data.Nutrients.map((n) => ({
        attributeCode: n.attributeCode,
        nutrientCode: n.nutrient?.nutrientCode ?? '',
      }))
    );
  };

  return isError ? (
    <Error error={error} />
  ) : !data || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <FeedManagementTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id="general.confidential" />
          </Typography>
        }
        utils={<Breadcrumbs />}
      />

      <form onSubmit={handleSubmit(handleUpdateNutrientsAttribute)}>
        <Paper transparent>
          <Typography variant="h3" color={theme.palette.common.white} pb={4}>
            <FormattedMessage id={`${PAGE_NAME_ID}.title`} />
          </Typography>
          <Paper>
            <Grid container spacing={4}>
              {fields?.map((field, idx) => (
                <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                  <Autocomplete
                    dark
                    fullWidth
                    label={intl.formatMessage({
                      id: `${PAGE_NAME_ID}.${field.attributeCode}Label`,
                    })}
                    options={nutrients ?? []}
                    getOptionLabel={(option) => `${option.nutrientCode}  ${option.nutrientDescription}`}
                    controller={{
                      name: `Nutrients.${idx}.nutrient`,
                      control: control,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
          <Box display="flex" justifyContent="flex-end" p={theme.spacing(4, 0)}>
            <Button type="submit" disabled={updateNutrientsAttribute.isPending}>
              {updateNutrientsAttribute.isPending ? (
                <CircularProgress color="inherit" />
              ) : (
                <FormattedMessage
                  id="general.submit"
                  values={{ type: intl.formatMessage({ id: 'feedManagement.navigation.listNutrients' }) }}
                />
              )}
            </Button>
          </Box>
        </Paper>
      </form>
    </>
  );
};

export default ListNutrients;
